import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ElLoading, ElMessage } from 'element-plus'
import { env } from '@/config/env'
import { clearAuth, getRefreshToken, getToken, saveToken } from '@/utils/auth'
import type { ApiResponse, FileResult, TokenVO } from './types'

/** 扩展配置 */
export interface RequestOptions extends AxiosRequestConfig {
  /** 是否开启全局 loading，默认 true */
  loading?: boolean
  /** 是否携带 token，默认 true */
  withToken?: boolean
  /** 是否拦截重复提交，默认 false */
  preventDuplicate?: boolean
  /** 路由切换时是否自动取消，默认 true */
  cancelable?: boolean
  /** 是否静默失败（不弹 ElMessage），默认 false */
  silent?: boolean
  /** 返回原始 AxiosResponse */
  raw?: boolean
}

type LoadingInstance = { close: () => void }

const DEFAULT_TIMEOUT = 30_000

const instance: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: DEFAULT_TIMEOUT,
  headers: { 'Content-Type': 'application/json;charset=UTF-8' },
})

// ---------------- 全局 loading 计数 ----------------
let loadingCount = 0
let loadingInstance: LoadingInstance | null = null

function showLoading(): void {
  loadingCount += 1
  if (loadingInstance) return
  loadingInstance = ElLoading.service({
    lock: true,
    text: '加载中…',
    background: 'rgba(0, 0, 0, 0.08)',
  }) as unknown as LoadingInstance
}

function hideLoading(): void {
  loadingCount = Math.max(0, loadingCount - 1)
  if (loadingCount === 0 && loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
}

// ---------------- 请求取消 / 重复提交 ----------------
const pendingMap = new Map<string, AbortController>()
const duplicateMap = new Map<string, number>()

function buildKey(config: AxiosRequestConfig): string {
  return [
    config.method ?? 'get',
    config.url ?? '',
    JSON.stringify(config.params ?? {}),
    typeof config.data === 'string' ? config.data : JSON.stringify(config.data ?? {}),
  ].join('|')
}

/** 路由切换时调用：取消所有未完成请求 */
export function cancelAllRequests(reason = 'route-changed'): void {
  pendingMap.forEach((controller) => controller.abort(reason))
  pendingMap.clear()
  duplicateMap.clear()
  if (loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
  loadingCount = 0
}

// ---------------- 401 / 刷新令牌 ----------------
let unauthorizedHandler: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler
}

let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing
  refreshing = (async () => {
    try {
      const refreshToken = getRefreshToken()
      if (!refreshToken) return null
      const res = await axios.post<ApiResponse<TokenVO>>(
        `${env.apiBaseUrl}/v1/auth/refresh`,
        {},
        { headers: { 'X-Refresh-Token': refreshToken }, timeout: 10_000 }
      )
      if (res.data.code === 200 && res.data.data?.accessToken) {
        saveToken({
          accessToken: res.data.data.accessToken,
          refreshToken: res.data.data.refreshToken ?? refreshToken,
        })
        return res.data.data.accessToken
      }
      return null
    } catch {
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

// ---------------- 拦截器 ----------------
type RequestConfigWithMeta = InternalAxiosRequestConfig & RequestOptions & { requestKey?: string }

instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const opts = config as RequestConfigWithMeta
    if (opts.loading !== false) showLoading()

    if (opts.withToken !== false) {
      const token = getToken()
      if (token) config.headers.Authorization = `Bearer ${token}`
    }

    const key = buildKey(config)

    if (opts.preventDuplicate) {
      const last = duplicateMap.get(key) ?? 0
      if (Date.now() - last < 1000) {
        const controller = pendingMap.get(key)
        controller?.abort('duplicate')
        hideLoading()
        ElMessage.warning('操作过于频繁，请稍后再试')
        return Promise.reject(new axios.Cancel('重复提交已拦截'))
      }
      duplicateMap.set(key, Date.now())
    }

    if (opts.cancelable !== false) {
      const controller = new AbortController()
      config.signal = controller.signal
      opts.requestKey = key
      pendingMap.set(key, controller)
    }

    return config
  },
  (error: unknown) => {
    hideLoading()
    return Promise.reject(error)
  }
)

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const opts = response.config as RequestConfigWithMeta
    if (opts.requestKey) pendingMap.delete(opts.requestKey)
    if (opts.loading !== false) hideLoading()
    return response
  },
  async (error: unknown) => {
    const axiosError = error as AxiosError<ApiResponse<unknown>>
    const opts = (axiosError.config ?? {}) as RequestConfigWithMeta
    if (opts.requestKey) pendingMap.delete(opts.requestKey)
    if (opts.loading !== false) hideLoading()

    if (axios.isCancel(error)) return Promise.reject(error)

    // 网络错误 / 超时
    if (!axiosError.response) {
      if (!opts.silent) ElMessage.error('网络异常，请检查网络连接')
      return Promise.reject(error)
    }

    const status = axiosError.response.status
    const data = axiosError.response.data

    if (status === 401) {
      const newToken = await refreshAccessToken()
      if (newToken && axiosError.config) {
        axiosError.config.headers.Authorization = `Bearer ${newToken}`
        return instance.request(axiosError.config)
      }
      clearAuth()
      unauthorizedHandler?.()
      if (!opts.silent) ElMessage.error('登录已失效，请重新登录')
      return Promise.reject(error)
    }

    if (status === 403) {
      // 无权限：只提示一次，前端路由已做拦截
      if (!opts.silent) ElMessage.error(data?.message || '无操作权限')
      return Promise.reject(error)
    }

    if (status === 429) {
      if (!opts.silent) ElMessage.warning('操作过于频繁，请稍后再试')
      return Promise.reject(error)
    }

    const message =
      data?.message || (status >= 500 ? '服务异常，请稍后重试' : `请求失败（${status}）`)
    if (!opts.silent) ElMessage.error(message)
    return Promise.reject(error)
  }
)

// ---------------- 统一拆包 ----------------
function isBlobResponse(response: AxiosResponse): boolean {
  return response.data instanceof Blob
}

function unwrap<T>(response: AxiosResponse, options: RequestOptions): T {
  if (options.raw) return response as unknown as T
  if (isBlobResponse(response)) {
    const disposition = response.headers?.['content-disposition'] as string | undefined
    const filenameMatch = disposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition) : null
    return {
      blob: response.data as Blob,
      filename: filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1]) : '',
    } as unknown as T
  }
  const body = response.data as ApiResponse<T> | null
  if (body && typeof body === 'object' && 'code' in body) {
    if (body.code === 200) return body.data
    if (!options.silent) ElMessage.error(body.message || '业务处理失败')
    throw new Error(body.message || '业务处理失败')
  }
  return response.data as T
}

async function request<T>(options: RequestOptions): Promise<T> {
  const response = await instance.request<unknown, AxiosResponse<ApiResponse<T>>>(options)
  return unwrap<T>(response, options)
}

export const http = {
  request,
  get<T>(url: string, params?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({ ...options, url, method: 'get', params })
  },
  post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({ ...options, url, method: 'post', data })
  },
  put<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({ ...options, url, method: 'put', data })
  },
  patch<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({ ...options, url, method: 'patch', data })
  },
  delete<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({ ...options, url, method: 'delete', data })
  },
  /** 下载文件流：自动识别文件名 */
  download<T = FileResult>(url: string, params?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>({
      ...options,
      url,
      method: 'get',
      params,
      responseType: 'blob',
      loading: options?.loading ?? false,
    })
  },
}

export default http
